import {
  getIncompleteClaims,
  getInProgressClaims,
  getSelfServiceToggles,
  getInsurancePolicyRenewal,
  getInsurancePolicyCurrent,
  requestCoCDocument,
  getDownloadCoC,
  getDownloadDocument,
  initializeAvokaForm,
  getQuoteSessionIdByCoverage,
  getQuoteSessionId,
  getPaymentDetails,
  createQuoteSession,
  orderPhysicalCardUrl,
  getPdsOrSpdsUrl,
  communicationsPreferencesUrl,
  getSubscriptions,
  updateSubscriptionOptIn,
  updateMembershipCardFormatPref,
} from '@/constants/forms-api.const';
import { RequestServices as _http } from '../common/request.services';
import { InsurancePolicy, PetCoverage, VehicleCoverage } from '@/interfaces';
import { IPaymentInstrument } from '@/interfaces/self-service/defer-payment.interface';
import { IPdsDocumentResponse } from '@/interfaces/entities/pds-document-response.interface';
import { CommunicationPreferences, Subscription } from '@/interfaces/self-service/communication-preferences';
import { isIosSafari } from '@/utils/common-helper';

export const SelfServiceService = {
  /**
   * Get claims incomplete
   * @param policyNumber
   * @returns
   */
  getIncompleteClaims(policyNumbers: string[]): Promise<any> {
    return _http.HttpPost(getIncompleteClaims, { policyNumbers: policyNumbers });
  },

  /**
   * Get claims in progress
   * @param policyNumbers
   */
  getInProgressClaims(policyNumbers: string[]): Promise<any> {
    return _http.HttpPost(getInProgressClaims, { policyNumbers: policyNumbers });
  },

  /**
   * Get claims in progress
   */
  getSelfServiceToggles(): Promise<any> {
    return _http.HttpGet(getSelfServiceToggles);
  },

  /**
   * Get renewal policy
   * @param index
   */
  getInsurancePolicyRenewal(policyNumber: string): Promise<any> {
    return _http.HttpGet(getInsurancePolicyRenewal.replace('{0}', policyNumber));
  },

  /**
   * Get current policy
   * @param index
   */
  getInsurancePolicyCurrent(policyNumber: string): Promise<any> {
    return _http.HttpGet(getInsurancePolicyCurrent.replace('{0}', policyNumber));
  },

  /**
   * Download CoC
   */
  getDownloadCoC(documentId: string) {
    window.open(getDownloadCoC.replace('{0}', documentId));
  },

  /**
   * Download Document
   */
  getDownloadDocument(documentId: string) {
    window.open(getDownloadDocument.replace('{0}', documentId));
  },

  /**
   * Request and download CoC Document
   */
  async requestCoCDocument(policyNumber: string): Promise<any> {
    try {
      const res = await _http.HttpGet(requestCoCDocument.replace('{0}', policyNumber));
      if (!res?.documentId) {
        throw new Error('Unable to retrieve documentId');
      }
      const downloadUrl = getDownloadCoC.replace('{0}', res.documentId);

      if (isIosSafari()) {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${res.documentId}-coc.pdf`;
        document.body.appendChild(a);
        a.click();

        // Clean-up
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        return;
      } else {
        // Non-iOS Safari browsers
        window.open(downloadUrl);
      }
    } catch (error) {
      console.error('CoC download error:', error);
    }
  },

  initializeAvokaForm(insurancePolicy: InsurancePolicy, coverage: VehicleCoverage | PetCoverage): Promise<any> {
    return _http.HttpPost(initializeAvokaForm, { insurancePolicy: insurancePolicy, vehicleCoverage: coverage });
  },

  getQuoteSessionIdByCoverage(policyNumber: string, quoteSessionType: string, coverageId: string): Promise<any> {
    return _http.HttpGet(
      getQuoteSessionIdByCoverage
        .replace('{0}', policyNumber)
        .replace('{1}', quoteSessionType)
        .replace('{2}', coverageId)
    );
  },

  getQuoteSessionId(policyNumber: string, quoteSessionType: string): Promise<any> {
    return _http.HttpGet(getQuoteSessionId.replace('{0}', policyNumber).replace('{1}', quoteSessionType));
  },

  createQuoteSession(policyNumber: string, page: string, coverageId?: string): Promise<any> {
    return _http.HttpPost(createQuoteSession, {
      policyNumber: policyNumber,
      coverageId: coverageId ?? null,
      page: page,
    });
  },

  /**
   * Returns the PDS or SPDS document url and title for the specified version.
   */
  getPdsOrSpdsUrl(version: string): Promise<IPdsDocumentResponse | null> {
    return _http.HttpGet(getPdsOrSpdsUrl.replace('{0}', version));
  },

  /**
   * Retrieve payment methods for a Direct Debit policy
   */
  getPaymentMethods(policyNumber: string, currentTerm: number): Promise<IPaymentInstrument> {
    return _http.HttpGet(getPaymentDetails.replace('{0}', policyNumber).replace('{1}', currentTerm?.toString()));
  },

  /**
   * Order physical membership card
   */
  orderPhysicalCard() {
    return _http.HttpPost(orderPhysicalCardUrl);
  },

  communicationsPreferences(d365Id: string, request: CommunicationPreferences) {
    return _http.HttpPost(communicationsPreferencesUrl.replace('{0}', d365Id), request);
  },

  getSubscriptions(d365Id: string) {
    return _http.HttpGet(getSubscriptions.replace('{0}', d365Id));
  },

  updateSubscriptionOptIn(d365Id: string, request: Subscription) {
    return _http.HttpPost(updateSubscriptionOptIn.replace('{0}', d365Id), request);
  },

  updateMembershipCardFormatPref(d365Id: string, request: CommunicationPreferences) {
    return _http.HttpPost(updateMembershipCardFormatPref.replace('{0}', d365Id), request);
  },
};
